import axios from "axios";

/*
 * Історія 15s / 30s свічок.
 *
 * Binance Futures не має секундних kline, тому свічки
 * будуємо з історії угод (aggTrades). Ці запити ділять
 * IP-ліміт Binance з усім іншим, тому:
 * - перше завантаження бере кілька сторінок і не чекає ліміту — графік з'являється одразу.
 *   Графік просить історію, поки не набере свою кількість свічок, тому, доки решта
 *   першого відрізка догружається у фоні, на старіші відрізки відповідаємо «даних немає»,
 *   а графік час від часу перечитує вже завантажене з кешу — історія росте на очах;
 * - старішу історію графік просить сам, коли її треба показати (заповнити екран
 *   чи гортання вліво), і ми покриваємо кожен такий відрізок повністю;
 * - на весь сайт не більше HISTORY_REQUESTS_PER_MINUTE запитів історії на хвилину:
 *   коли ліміт вичерпано, догрузка чекає, поки він поновиться;
 * - кеш угод доповнює живий потік, тож перемикання 15s ↔ 30s запитів не робить;
 * - після 429/418 від Binance запити чекають, скільки він сказав.
 */

const AGG_TRADES_URL =
  "https://fapi.binance.com/fapi/v1/aggTrades";
const TRADES_PER_REQUEST = 1000;
// aggTrades важить 20 з 2400 на хвилину: 40 запитів лишають дві третини ліміту решті сайту
const HISTORY_REQUESTS_PER_MINUTE = 40;
// Свіжа сторінка угод для нової пари може взяти ще трохи, але не більше половини ліміту
const TOTAL_REQUESTS_PER_MINUTE = 60;
const BUDGET_WINDOW_MS = 60 * 1000;
// Номери угод ідуть підряд, тому сторінки можна просити паралельно
const PARALLEL_REQUESTS = 10;
// Один паралельний запит на старті: графік з'являється за пів секунди
const INITIAL_PAGES = 5;
// Межа пам'яті на пару: по BTC це година-дві навіть в активний ринок
const MAX_CACHED_TRADES = 300000;
// Як часто графік перечитує історію, що росте у фоні: частіше — помітно блимає
const EXTEND_NOTIFY_INTERVAL_MS = 10000;
// Кеш, у який живий потік писав нещодавно, не треба оновлювати запитом
const LIVE_FRESH_MS = 5000;
const RATE_LIMIT_STATUSES = [418, 429];
const DEFAULT_RETRY_AFTER_SECONDS = 60;

const SECONDS_RESOLUTION_PATTERN = /^(\d+)S$/;

/**
 * @param {string} resolution chart resolution, e.g. "15S"
 * @returns {number | null} candle length in ms for second resolutions
 */
export const getSecondsIntervalMs = (resolution) => {
  const match = SECONDS_RESOLUTION_PATTERN.exec(String(resolution));
  return match ? Number(match[1]) * 1000 : null;
};

const requestTimestamps = [];
let blockedUntil = 0;
const cacheBySymbol = new Map();
const extendListeners = new Set();

const sleep = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const dropExpiredRequests = (now) => {
  while (
    requestTimestamps.length &&
    now - requestTimestamps[0] >= BUDGET_WINDOW_MS
  ) {
    requestTimestamps.shift();
  }
};

const tryTakeBudget = (limit) => {
  const now = Date.now();
  if (now < blockedUntil) return false;

  dropExpiredRequests(now);
  if (requestTimestamps.length >= limit) return false;

  requestTimestamps.push(now);
  return true;
};

const waitForBudget = async (limit) => {
  while (!tryTakeBudget(limit)) {
    const now = Date.now();
    const freesAt =
      now < blockedUntil
        ? blockedUntil
        : requestTimestamps[requestTimestamps.length - limit] +
          BUDGET_WINDOW_MS;

    await sleep(Math.max(freesAt - now, 0));
  }
};

const toTrade = (row) => ({
  id: Number(row.a),
  time: Number(row.T),
  price: Number(row.p),
  quantity: Number(row.q),
});

// Budget must be taken by the caller
const requestTradesPage = async (symbol, params) => {
  try {
    const { data } = await axios.get(AGG_TRADES_URL, {
      params: {
        symbol,
        limit: TRADES_PER_REQUEST,
        ...params,
      },
    });

    return Array.isArray(data) ? data.map(toTrade) : [];
  } catch (error) {
    const status = error?.response?.status;

    if (RATE_LIMIT_STATUSES.includes(status)) {
      const retryAfterSeconds =
        Number(error.response.headers?.["retry-after"]) ||
        DEFAULT_RETRY_AFTER_SECONDS;

      blockedUntil = Date.now() + retryAfterSeconds * 1000;
    }

    throw error;
  }
};

const mergeTrades = (...lists) =>
  Array.from(
    new Map(
      lists.flat().map((trade) => [trade.id, trade]),
    ).values(),
  ).sort((a, b) => a.id - b.id);

const getCache = (symbol) => {
  if (!cacheBySymbol.has(symbol)) {
    cacheBySymbol.set(symbol, {
      trades: [],
      isExhausted: false,
      lastLiveAt: 0,
      // Start of the first range that was answered only partly
      uncoveredUntilMs: null,
      isCoveringInBackground: false,
      lastExtendNotifyAt: 0,
      // Oldest candle the chart has received since its last first request
      servedFromMs: null,
      // Loads of one pair run one after another, so they never fetch the same pages
      loadQueue: Promise.resolve(),
    });
  }

  return cacheBySymbol.get(symbol);
};

/*
 * Свіжі угоди перед першим запитом графіка.
 * Якщо між кешем і новими угодами є розрив,
 * кеш застарів — починаємо історію заново.
 */
const refreshLatestTrades = async (symbol, shouldWait = false) => {
  if (shouldWait) {
    await waitForBudget(TOTAL_REQUESTS_PER_MINUTE);
  } else if (!tryTakeBudget(TOTAL_REQUESTS_PER_MINUTE)) {
    return;
  }

  const cache = getCache(symbol);
  const latestTrades = await requestTradesPage(symbol, {});

  if (!latestTrades.length) return;

  const newestCachedId =
    cache.trades[cache.trades.length - 1]?.id;
  const hasGap =
    newestCachedId == null ||
    latestTrades[0].id > newestCachedId + 1;

  if (hasGap) {
    cache.trades = latestTrades;
    cache.isExhausted = false;
    return;
  }

  cache.trades = mergeTrades(cache.trades, latestTrades);
};

/**
 * Loads pages older than the cache, newest first.
 * @param {string} symbol
 * @param {object} options
 * @param {number} options.untilTimeMs stop once the oldest trade is at or before this time
 * @param {number} options.maxPages
 * @param {boolean} options.shouldWait wait for the budget, or stop when it runs out
 */
const loadOlderTrades = async (
  symbol,
  { untilTimeMs, maxPages, shouldWait },
) => {
  const cache = getCache(symbol);
  let pagesLoaded = 0;

  while (
    !cache.isExhausted &&
    cache.trades.length &&
    cache.trades[0].time > untilTimeMs &&
    pagesLoaded < maxPages
  ) {
    if (cache.trades.length >= MAX_CACHED_TRADES) {
      cache.isExhausted = true;
      break;
    }

    const oldestId = cache.trades[0].id;

    const candidateFromIds = Array.from(
      {
        length: Math.min(
          PARALLEL_REQUESTS,
          maxPages - pagesLoaded,
        ),
      },
      (_, index) =>
        oldestId - TRADES_PER_REQUEST * (index + 1),
    ).filter((fromId) => fromId + TRADES_PER_REQUEST > 0);

    if (!candidateFromIds.length) {
      cache.isExhausted = true;
      break;
    }

    const fromIds = [];
    for (const fromId of candidateFromIds) {
      if (shouldWait) {
        await waitForBudget(HISTORY_REQUESTS_PER_MINUTE);
      } else if (!tryTakeBudget(HISTORY_REQUESTS_PER_MINUTE)) {
        break;
      }
      fromIds.push(fromId);
    }

    if (!fromIds.length) break;

    const pages = await Promise.all(
      fromIds.map((fromId) =>
        requestTradesPage(symbol, {
          fromId: Math.max(0, fromId),
        }),
      ),
    );

    const olderTrades = pages
      .flat()
      .filter((trade) => trade.id < oldestId);

    pagesLoaded += fromIds.length;

    if (!olderTrades.length) {
      cache.isExhausted = true;
      break;
    }

    cache.trades = mergeTrades(olderTrades, cache.trades);

    if (fromIds.length < candidateFromIds.length) break;
  }
};

const runExclusive = (cache, task) => {
  const run = cache.loadQueue.then(task);
  cache.loadQueue = run.catch(() => {});
  return run;
};

const isFirstRangeCovered = (cache) => {
  const oldestTrade = cache.trades[0];

  return (
    cache.uncoveredUntilMs == null ||
    cache.isExhausted ||
    (Boolean(oldestTrade) && oldestTrade.time <= cache.uncoveredUntilMs)
  );
};

const notifyExtended = (symbol) => {
  getCache(symbol).lastExtendNotifyAt = Date.now();
  extendListeners.forEach((listener) => listener(symbol));
};

// Loads the rest of the first range a chunk at a time, letting the chart redraw as it grows
const coverInBackground = async (symbol) => {
  const cache = getCache(symbol);
  if (cache.isCoveringInBackground) return;

  cache.isCoveringInBackground = true;

  try {
    // The budget was spent before the first answer: wait for it instead of finishing empty
    if (!cache.trades.length) {
      await runExclusive(cache, () => refreshLatestTrades(symbol, true));
    }

    while (!isFirstRangeCovered(cache)) {
      const oldestIdBefore = cache.trades[0]?.id;

      await runExclusive(cache, () =>
        loadOlderTrades(symbol, {
          untilTimeMs: cache.uncoveredUntilMs,
          maxPages: PARALLEL_REQUESTS,
          shouldWait: true,
        }),
      );

      if (cache.trades[0]?.id === oldestIdBefore) break;

      if (isFirstRangeCovered(cache)) break;

      if (
        Date.now() - cache.lastExtendNotifyAt >=
        EXTEND_NOTIFY_INTERVAL_MS
      ) {
        notifyExtended(symbol);
      }
    }

    cache.uncoveredUntilMs = null;
    notifyExtended(symbol);
  } catch {
    // The next first request of the chart starts over
    cache.uncoveredUntilMs = null;
  } finally {
    cache.isCoveringInBackground = false;
  }
};

const tradesToBars = (trades, intervalMs) => {
  const barsByTime = new Map();

  trades.forEach(({ time, price, quantity }) => {
    const barTime = Math.floor(time / intervalMs) * intervalMs;
    const bar = barsByTime.get(barTime);

    if (!bar) {
      barsByTime.set(barTime, {
        time: barTime,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: quantity,
      });
      return;
    }

    bar.high = Math.max(bar.high, price);
    bar.low = Math.min(bar.low, price);
    bar.close = price;
    bar.volume += quantity;
  });

  return Array.from(barsByTime.values()).sort(
    (a, b) => a.time - b.time,
  );
};

/**
 * @param {string} symbol e.g. "BTCUSDT"
 * @param {number} intervalMs 15000 or 30000
 * @param {{from: number, to: number, firstDataRequest: boolean}} periodParams period of the chart, seconds
 * @returns {Promise<{bars: object[], noData: boolean}>}
 */
export const getSecondsBars = async (
  symbol,
  intervalMs,
  { from, to, firstDataRequest },
) => {
  const cache = getCache(symbol);
  const fromMs = from * 1000;
  const toMs = to * 1000;
  const isFedByStream =
    Date.now() - cache.lastLiveAt < LIVE_FRESH_MS;

  if (
    firstDataRequest &&
    !isFedByStream &&
    !cache.isCoveringInBackground
  ) {
    await runExclusive(cache, () => refreshLatestTrades(symbol));
  }

  // The chart fills its first screen with these older requests. Until the background load is
  // done, answer at once so it can draw: from the cache when it has the whole range, otherwise
  // "no data" — the next re-read brings that part
  const cachedOldestTrade = cache.trades[0];
  const isRangeCached =
    Boolean(cachedOldestTrade) && cachedOldestTrade.time <= fromMs;

  if (
    !firstDataRequest &&
    cache.isCoveringInBackground &&
    !isRangeCached
  ) {
    return { bars: [], noData: true };
  }

  // A re-read during the background load answers from the cache, not behind that load
  if (!cache.isCoveringInBackground) {
    await runExclusive(cache, () =>
      loadOlderTrades(symbol, {
        untilTimeMs: fromMs,
        // The first answer goes out at once; older ranges are covered whole, waiting for the budget
        maxPages: firstDataRequest ? INITIAL_PAGES : Infinity,
        shouldWait: !firstDataRequest,
      }),
    );
  }

  const oldestTrade = cache.trades[0];
  const isPartial =
    !cache.isExhausted && (!oldestTrade || oldestTrade.time > fromMs);

  if (firstDataRequest && isPartial) {
    cache.uncoveredUntilMs = Math.min(
      cache.uncoveredUntilMs ?? fromMs,
      fromMs,
    );
    coverInBackground(symbol);
  }

  // A candle that starts before the oldest loaded trade misses some of its trades
  const firstCompleteBarTime =
    oldestTrade && !cache.isExhausted
      ? Math.ceil(oldestTrade.time / intervalMs) * intervalMs
      : -Infinity;

  // The chart needs no candles older than `from`. Later answers give only candles older than
  // those already given, so none repeat.
  const lowerBoundMs = Math.max(fromMs, firstCompleteBarTime);
  const upperBoundMs = firstDataRequest
    ? toMs
    : Math.min(toMs, cache.servedFromMs ?? toMs);
  const alignedLowerBoundMs =
    Math.floor(Math.max(lowerBoundMs, 0) / intervalMs) * intervalMs;

  // Whole candles: a candle at the upper bound may have trades after it
  const bars = tradesToBars(
    cache.trades.filter(
      ({ time }) =>
        time >= alignedLowerBoundMs && time < upperBoundMs + intervalMs,
    ),
    intervalMs,
  ).filter(
    ({ time }) => time >= lowerBoundMs && time < upperBoundMs,
  );

  if (bars.length) {
    cache.servedFromMs = firstDataRequest
      ? bars[0].time
      : Math.min(cache.servedFromMs ?? bars[0].time, bars[0].time);
  }

  return {
    bars,
    noData: !bars.length && cache.isExhausted,
  };
};

/**
 * Called with the symbol when the history loaded in the background has grown:
 * the chart should re-read its data, it comes from the cache without requests.
 * @param {(symbol: string) => void} listener
 * @returns {() => void} stop listening
 */
export const onSecondsHistoryExtended = (listener) => {
  extendListeners.add(listener);
  return () => extendListeners.delete(listener);
};

/**
 * Keeps the cache of a pair current while its chart streams trades,
 * so switching 15s ↔ 30s needs no requests.
 * @param {string} symbol e.g. "BTCUSDT"
 * @param {{a: number, T: number, p: string, q: string}} aggTrade stream payload
 */
export const appendLiveTrade = (symbol, aggTrade) => {
  const cache = cacheBySymbol.get(symbol);
  if (!cache?.trades.length) return;

  const trade = toTrade(aggTrade);
  const newestId = cache.trades[cache.trades.length - 1].id;

  // A missed trade leaves a hole; the next first request starts the history again
  if (trade.id !== newestId + 1) {
    if (trade.id > newestId + 1) cache.lastLiveAt = 0;
    return;
  }

  cache.trades.push(trade);
  cache.lastLiveAt = Date.now();

  if (cache.trades.length > MAX_CACHED_TRADES) {
    cache.trades.splice(0, cache.trades.length - MAX_CACHED_TRADES);
  }
};

/**
 * The current candle from the loaded history, so the live candle continues it
 * instead of starting from the first streamed trade.
 * @param {string} symbol e.g. "BTCUSDT"
 * @param {number} intervalMs
 * @returns {{bar: object, lastTradeId: number} | null}
 */
export const getCurrentSecondsBar = (symbol, intervalMs) => {
  const { trades } = getCache(symbol);
  const lastTrade = trades[trades.length - 1];

  if (!lastTrade) return null;

  const barTime =
    Math.floor(lastTrade.time / intervalMs) * intervalMs;
  const barTrades = trades.filter(
    ({ time }) => time >= barTime,
  );

  return {
    bar: tradesToBars(barTrades, intervalMs)[0],
    lastTradeId: lastTrade.id,
  };
};
